import type {
  AwardSummary,
  Me,
  MyRecognition,
  PoolPositions,
  RecognizeOptions,
  SectorRecognition,
  StockAdminQueue,
  Waiting,
} from '@myrewards/contracts';
import type { HomeData, HomeView } from '../api/home';
import type { PoolPosition } from './labels';
import { homeLayout, homeSectorAssignments } from './layout';
import { fiscalYearOn } from '../ui/fiscal';

type Ref = { id: string; name: string };

/** One sector panel (HOME1): the person's role there, the real pool, the recognition cards. */
export interface SectorPanelModel {
  sector: Ref;
  role: 'sector_lead' | 'delegate';
  /** Delegates: the Sector Lead they enter for, and whether entries need that lead's OK. */
  forPerson: Ref | null;
  needsApproval: boolean | null;
  /** `null` when the person can't see the pool (or it doesn't exist). */
  position: PoolPosition | null;
  fiscalYear: number;
  recognition: SectorRecognition;
  /** What this person can start for this sector (Recognize options). */
  offers: RecognizeOptions['entries'];
  /**
   * The sector's day one (FR2): nothing awarded from its pool, nothing held, nobody recognized in
   * the window. From the data, never from pending getting-started steps.
   */
  nothingYet: boolean;
}

type Units = { cashCents: number; options: number };

/** X1 / FR3: the company's pools for the year and what's waiting on the Executive group. */
export interface ExecutiveModel {
  fiscalYear: number;
  /** Every pool the person may see: the Executive pool, the sector pools, then PAA/TAA. */
  pools: PoolPosition[];
  /** The Executive pool and the sector pools together. */
  discretionary: {
    pools: number;
    /** Pools with a budget; `null` while none has one. */
    budget: Units | null;
    left: Units | null;
    awarded: Units;
  };
  paaTaa: PoolPosition | null;
  poolsSet: number;
  /** Waiting on any one Executive: overages, and PAAs, TAAs and requests to decide. */
  approvals: { total: number; overage: number; PAA: number; TAA: number; EDB: number };
  /** Nothing awarded from any pool this year (FR3 / X1's first day). From the data only. */
  nothingYet: boolean;
}

/** R1: a pre-approved requester's Executive Discretionary Bonus requests. */
export interface RequesterModel {
  /** The year the requests are from (this year, in the organization's calendar). */
  year: number;
  requests: AwardSummary[];
  /** Sent, and waiting on the Executives' decision outside the app. */
  waitingOnExecutives: AwardSummary[];
  /** Past the decision (with the Stock Admin team, paid or closed). */
  awarded: { requests: number; people: number; cashCents: number; options: number };
  nothingYet: boolean;
}

/** Which Home this is: the ops queue (HOME2), a blended Home with role panels (HOME1), or E1. */
export type HomeLayout = 'queue' | 'blended' | 'finance' | 'employee';

export interface HomeModel {
  layout: HomeLayout;
  asOf: string;
  /**
   * The person's own day one (FR1): no awards received, ever. From the data, never from pending
   * getting-started steps, which only add rows to Waiting on you (QA-001).
   */
  nothingYet: boolean;
  waiting: Waiting['items'];
  sectors: SectorPanelModel[];
  mine: MyRecognition;
  queue: StockAdminQueue | null;
  /** Whether any award has ever reached the queue; `null` when unknown or not a Stock Admin. */
  queueHistory: boolean | null;
  executive: ExecutiveModel | null;
  requester: RequesterModel | null;
  options: RecognizeOptions;
}

const KIND_ORDER = { executive: 0, sector: 1, paa_taa: 2, spot: 3 } as const;

function addUnits(a: Units, b: Units): Units {
  return { cashCents: a.cashCents + b.cashCents, options: a.options + b.options };
}

function used(p: PoolPosition): Units {
  return addUnits(p.paid, p.inProgress);
}

/**
 * Home's pool order: the Executive pool, the sector pools as sent, the PAA/TAA pool, then the Spot
 * Awards pool (APP-264).
 */
export function poolsInOrder<P extends PoolPosition>(pools: readonly P[]): P[] {
  return [...pools].sort((a, b) => KIND_ORDER[a.kind ?? 'sector'] - KIND_ORDER[b.kind ?? 'sector']);
}

/** The Executive's view of the pools and approvals (X1). */
export function executiveModel(
  positions: PoolPositions,
  waiting: Waiting['items'],
  firstRun: boolean,
): ExecutiveModel {
  const pools = poolsInOrder(positions.pools);
  // The Executive and sector pools: not the PAA/TAA pool, nor the Spot Awards pool (APP-264).
  const discretionaryPools = pools.filter((p) => p.kind === 'executive' || p.kind === 'sector');
  const withBudget = discretionaryPools.filter((p) => p.budget !== null);
  const zero: Units = { cashCents: 0, options: 0 };
  const budget =
    withBudget.length === 0
      ? null
      : withBudget.reduce((sum, p) => addUnits(sum, p.budget ?? zero), zero);
  const awarded = discretionaryPools.reduce((sum, p) => addUnits(sum, used(p)), zero);
  const approvals = { total: 0, overage: 0, PAA: 0, TAA: 0, EDB: 0 };
  for (const item of waiting) {
    if (item.type === 'approve_overage') {
      approvals.total += 1;
      approvals.overage += 1;
    } else if (item.type === 'executive_decision') {
      approvals.total += 1;
      if (item.award.type !== 'SDB') approvals[item.award.type] += 1;
    }
  }
  const nothingUsed = pools.every((p) => used(p).cashCents === 0 && used(p).options === 0);
  return {
    fiscalYear: positions.fiscalYear,
    pools,
    discretionary: {
      pools: discretionaryPools.length,
      budget,
      left:
        budget === null
          ? null
          : {
              cashCents: budget.cashCents - awarded.cashCents,
              options: budget.options - awarded.options,
            },
      awarded,
    },
    paaTaa: pools.find((p) => p.kind === 'paa_taa') ?? null,
    poolsSet: pools.filter((p) => p.budget !== null).length,
    approvals,
    nothingYet: firstRun || nothingUsed,
  };
}

const DECIDED: ReadonlySet<string> = new Set([
  'with_stock_admin',
  'employee_choice',
  'paid',
  'closed',
]);

/** A requester's own requests, and what the Executives have decided so far (R1). */
export function requesterModel({
  year,
  items: requests,
  earlier,
}: NonNullable<HomeData['requests']>): RequesterModel {
  const live = requests.filter((r) => r.ended === null);
  const decided = live.filter((r) => r.stage !== null && DECIDED.has(r.stage));
  return {
    year,
    requests,
    waitingOnExecutives: live.filter((r) => r.stage === 'approval'),
    awarded: {
      requests: decided.length,
      people: decided.reduce((sum, r) => sum + r.people, 0),
      cashCents: decided.reduce((sum, r) => sum + r.cashCents, 0),
      options: decided.reduce((sum, r) => sum + r.options, 0),
    },
    nothingYet: requests.length === 0 && !earlier,
  };
}

/** A sector the server sent nothing for: no holds; a delegate has no team snapshot. */
function emptyRecognition(sector: Ref, delegate: boolean): SectorRecognition {
  return {
    sector,
    holds: [],
    minimumOptionsPerPerson: 100,
    team: delegate
      ? null
      : {
          people: 0,
          teams: 0,
          recognized: 0,
          repeat: 0,
          notYet: 0,
          repeatRule: { awards: 3, cashCents: 500_000 },
          months: [],
          rows: [],
          mostRecognized: [],
          notRecognized: { count: 0, sample: [], biggestTeams: [] },
        },
  };
}

/** Nothing awarded or in progress from the pool this year. A pool nobody can see says nothing. */
function poolUnused(position: PoolPosition | null): boolean {
  if (position === null) return true;
  const { paid, inProgress } = position;
  return paid.cashCents + inProgress.cashCents === 0 && paid.options + inProgress.options === 0;
}

/** A sector with no history yet: the FR2 states. */
export function sectorNothingYet(
  recognition: SectorRecognition,
  position: PoolPosition | null,
): boolean {
  const team = recognition.team;
  const nobody =
    team === null || (team.recognized === 0 && team.months.every((m) => m.awards === 0));
  return recognition.holds.length === 0 && nobody && poolUnused(position);
}

/** Puts the server’s answers together into what Home renders. */
export function homeModel(me: Me, waiting: Waiting, data: HomeData, view: HomeView): HomeModel {
  // `?view=first-run` (development builds) shows every first-day state for design review.
  const firstRun = view === 'first-run';
  const fiscalYear = data.positions?.fiscalYear ?? fiscalYearOn(waiting.asOf);
  const sectors: SectorPanelModel[] = [];
  for (const assignment of homeSectorAssignments(me.roles)) {
    const sector = assignment.sector;
    if (!sector || sectors.some((s) => s.sector.id === sector.id)) continue;
    const position =
      data.positions?.pools.find((p) => p.pool.id === sector.id) ??
      data.positions?.pools.find((p) => p.pool.name === sector.name) ??
      null;
    const recognition =
      data.recognition.sectors.find((s) => s.sector.id === sector.id) ??
      emptyRecognition(sector, assignment.role === 'delegate');
    sectors.push({
      sector,
      role: assignment.role === 'sector_lead' ? 'sector_lead' : 'delegate',
      forPerson: assignment.forPerson,
      needsApproval: assignment.needsApproval,
      position,
      fiscalYear,
      recognition,
      offers: data.options.entries.filter((entry) => entry.sector?.id === sector.id),
      nothingYet: firstRun || sectorNothingYet(recognition, position),
    });
  }
  const mine = data.recognition.mine;
  const isExecutive = me.roles.some((a) => a.role === 'executive');
  const executive =
    isExecutive && data.positions ? executiveModel(data.positions, waiting.items, firstRun) : null;
  const requester = data.requests === null ? null : requesterModel(data.requests);
  return {
    layout: homeLayout(me.roles),
    asOf: waiting.asOf,
    nothingYet: firstRun || (mine.awards.length === 0 && mine.totals.awards === 0),
    waiting: waiting.items,
    sectors,
    mine: data.recognition.mine,
    queue: data.queue,
    queueHistory: data.queueHistory,
    executive,
    requester,
    options: data.options,
  };
}

/**
 * The sectors a Sector Lead leads: those with a sector pool. Corporate has people but no pool and
 * no Sector Lead, so it's left out of the Sector Leads list and its setup rule.
 */
export function ledSectors<T extends { id: string }>(
  sectors: readonly T[],
  pools: readonly PoolPosition[],
): T[] {
  const withPool = new Set(pools.filter((p) => p.kind === 'sector').map((p) => p.pool.id));
  return withPool.size === 0 ? [...sectors] : sectors.filter((s) => withPool.has(s.id));
}
